'use client';

import { c as _c } from "react/compiler-runtime";
import { DataGrid } from '@xefi/x-react/datagrid';
import { useDebouncedState } from '@xefi/x-react/hooks';
import { startTransition, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DataGridFooter } from '@/components/DataGridFooter';
import { PageContainer } from '@/components/PageContainer';
import { useStatusesQuery } from '@/hooks/useStatusesQuery';
import { useMainStore } from '@/store';
import { getDefaultSurveyTab } from '@/utils/surveyTabUtils';
import { useHomeFilters } from '../_hooks/useHomeFilters';
import { useSurveyColumns } from '../_hooks/useSurveyColumns';
import { useSurveyCounts } from '../_hooks/useSurveyCounts';
import { useSurveyRowAction } from '../_hooks/useSurveyRowAction';
import { useSurveysInfiniteQuery } from '../_hooks/useSurveysInfiniteQuery';
import { useSurveyTabs } from '../_hooks/useSurveyTabs';
import { DATA_GRID_CLASS_NAMES, FOOTER_CLASS_NAMES, HOME_CONSTANTS } from '../constants';
import { EmptySurveyState } from './EmptySurveyState';
import { ErrorDisplay } from './ErrorDisplay';
import { TabSurveys } from './TabSurveys';
export const Home = () => {
  const $ = _c(66);
  const {
    t
  } = useTranslation();
  const {
    currentUser,
    setSurveyTab
  } = useMainStore();
  const {
    data: t0
  } = useStatusesQuery();
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? [] : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const statuses = t1;
  let t2;
  if ($[2] !== setSurveyTab || $[3] !== statuses) {
    t2 = {
      statuses,
      setSurveyTab
    };
    $[2] = setSurveyTab;
    $[3] = statuses;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const {
    columns
  } = useSurveyColumns(t2);
  const {
    filters,
    setFilters,
    activeTab,
    setActiveTab
  } = useHomeFilters();
  const [inputValue, setInputValue] = useState("");
  const [search, setSearch] = useDebouncedState("", HOME_CONSTANTS.SEARCH_DEBOUNCE_DELAY);
  let t3;
  if ($[5] !== setSearch) {
    t3 = value => {
      setInputValue(value);
      setSearch(value);
    };
    $[5] = setSearch;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const handleSearchChange = t3;
  let t4;
  if ($[7] !== setSearch) {
    t4 = () => {
      setInputValue("");
      setSearch("");
    };
    $[7] = setSearch;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const resetSearch = t4;
  const {
    activeSurveysCount,
    closedSurveysCount,
    isLoadingActive,
    isLoadingClosed
  } = useSurveyCounts(filters, search);
  const {
    handleSurveyRowAction
  } = useSurveyRowAction();
  let t5;
  if ($[9] !== filters || $[10] !== search) {
    t5 = {
      filters,
      search
    };
    $[9] = filters;
    $[10] = search;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const {
    surveys,
    isLoading,
    isFetching,
    fetchNextPage,
    hasMoreData,
    total,
    onSortChange,
    error
  } = useSurveysInfiniteQuery(t5);
  let t6;
  if ($[12] !== activeSurveysCount || $[13] !== activeTab || $[14] !== closedSurveysCount || $[15] !== isLoadingActive || $[16] !== isLoadingClosed) {
    t6 = {
      activeSurveysCount,
      closedSurveysCount,
      isLoadingActive,
      isLoadingClosed,
      activeTabKey: activeTab
    };
    $[12] = activeSurveysCount;
    $[13] = activeTab;
    $[14] = closedSurveysCount;
    $[15] = isLoadingActive;
    $[16] = isLoadingClosed;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  const tabs = useSurveyTabs(t6);
  let t7;
  if ($[18] !== onSortChange) {
    t7 = (field, direction) => {
      startTransition(() => {
        onSortChange([{
          field: String(field),
          direction: direction as 'asc' | 'desc'
        }]);
      });
    };
    $[18] = onSortChange;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  const handleSortChange = t7;
  let t8;
  if ($[20] !== handleSurveyRowAction || $[21] !== setSurveyTab || $[22] !== surveys) {
    t8 = key => {
      const surveyId = String(key);
      const survey = surveys.find(s => s.id === surveyId);
      handleSurveyRowAction(surveyId, surveys);
      if (survey) {
        setSurveyTab(survey.gates?.authorizedToUpdate ? getDefaultSurveyTab(survey.entries_count) : "responses");
      }
    };
    $[20] = handleSurveyRowAction;
    $[21] = setSurveyTab;
    $[22] = surveys;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  const handleRowAction = t8;
  let t9;
  const t10 = surveys.length;
  let t11;
  if ($[24] !== t) {
    t11 = t("survey.nbActive_other");
    $[24] = t;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== surveys.length || $[27] !== t11 || $[28] !== total) {
    t12 = <DataGridFooter current={t10} total={total} label={t11} />;
    $[26] = surveys.length;
    $[27] = t11;
    $[28] = total;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  t9 = t12;
  const footerContent = t9;
  const isInitialLoading = isLoading || !currentUser?.id || !currentUser?.site?.client_id;
  const handleCellClick = _temp;
  let t13;
  let t14;
  if ($[30] !== surveys.length) {
    t14 = surveys.length === 0 ? <EmptySurveyState /> : null;
    $[30] = surveys.length;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== t14) {
    t15 = {
      emptyContent: t14
    };
    $[32] = t14;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = {
      onClick: handleCellClick,
      className: "select-text"
    };
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== t15) {
    t17 = {
      tableBodyProps: t15,
      tableCellProps: t16
    };
    $[35] = t15;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  let t18;
  if ($[37] !== columns || $[38] !== fetchNextPage || $[39] !== footerContent || $[40] !== handleRowAction || $[41] !== handleSortChange || $[42] !== hasMoreData || $[43] !== isFetching || $[44] !== isInitialLoading || $[45] !== surveys || $[46] !== t17) {
    t18 = {
      columns,
      rows: surveys,
      onSortChange: handleSortChange,
      isLoading: isInitialLoading,
      isFetching,
      hasMoreData,
      showBlurOnFetch: true,
      fetchNextPage,
      variant: "bordered" as const,
      className: "w-full",
      skeletonRowsCount: HOME_CONSTANTS.DEFAULT_SKELETON_ROWS,
      classNames: DATA_GRID_CLASS_NAMES,
      onRowAction: handleRowAction,
      childrenProps: t17,
      footerClassName: FOOTER_CLASS_NAMES.footer,
      footerContent
    };
    $[37] = columns;
    $[38] = fetchNextPage;
    $[39] = footerContent;
    $[40] = handleRowAction;
    $[41] = handleSortChange;
    $[42] = hasMoreData;
    $[43] = isFetching;
    $[44] = isInitialLoading;
    $[45] = surveys;
    $[46] = t17;
    $[47] = t18;
  } else {
    t18 = $[47];
  }
  t13 = t18;
  const dataGridProps = t13;
  let t19;
  if ($[48] !== t) {
    t19 = t("survey.mine");
    $[48] = t;
    $[49] = t19;
  } else {
    t19 = $[49];
  }
  let t20;
  if ($[50] !== activeTab || $[51] !== filters || $[52] !== handleSearchChange || $[53] !== inputValue || $[54] !== resetSearch || $[55] !== setActiveTab || $[56] !== setFilters || $[57] !== tabs) {
    t20 = <TabSurveys activeTab={activeTab} setActiveTab={setActiveTab} tabs={tabs} searchValue={inputValue} onSearchChange={handleSearchChange} filters={filters} setFilters={setFilters} resetSearch={resetSearch} />;
    $[50] = activeTab;
    $[51] = filters;
    $[52] = handleSearchChange;
    $[53] = inputValue;
    $[54] = resetSearch;
    $[55] = setActiveTab;
    $[56] = setFilters;
    $[57] = tabs;
    $[58] = t20;
  } else {
    t20 = $[58];
  }
  let t21;
  if ($[59] !== dataGridProps || $[60] !== error) {
    t21 = error ? <ErrorDisplay error={error} /> : <DataGrid {...dataGridProps} />;
    $[59] = dataGridProps;
    $[60] = error;
    $[61] = t21;
  } else {
    t21 = $[61];
  }
  let t22;
  if ($[62] !== t19 || $[63] !== t20 || $[64] !== t21) {
    t22 = <PageContainer title={t19}>{t20}{t21}</PageContainer>;
    $[62] = t19;
    $[63] = t20;
    $[64] = t21;
    $[65] = t22;
  } else {
    t22 = $[65];
  }
  return t22;
};
function _temp(event) {
  const selection = typeof window !== "undefined" ? window.getSelection?.()?.toString() : "";
  if (selection && selection.length > 0) {
    event.stopPropagation();
    event.preventDefault();
  }
}